import React, { useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useDispatch } from 'react-redux';

import { UniverseBuilder } from '@baemingo/cash-events';

import { useLogger, useSelector } from '~Hooks';
import * as actions from '~actions';
import { EVENT_TYPES } from '@baemingo/cash-events/dist/types';
import { useStorage } from '~Components/Storage';

/**
 * Create a context with an empty universe. This context acts as a
 * state that we can access from child components. We use this in app.tsx
 */
const Context = React.createContext(new UniverseBuilder().serialize());

export const CashUniverseProvider: React.FunctionComponent = ({ children }) => {
  // A local universe
  const universe = useRef(new UniverseBuilder());

  // The current index of which event we've added to the universe from redux
  const index = useRef(0);

  // Get events from state. Events are added via UI actions
  const events = useSelector((state) => state.cashEvents);

  // Get snapshot from state. If this changes we override our current universe
  const snapshot = useSelector((state) => state.cashSnapshot.snapshot);

  // We save the serialized universe in state to trigger re-renders
  const [state, setState] = useState(universe.current.serialize());

  const [storage] = useStorage();

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: CashUniverseProvider');
    return () => {
      mountLogger.info('Unmounted: CashUniverseProvider');
    };
  }, []);

  useEffect(() => {
    // Start by removing any events we've already added, then add the event
    events.slice(index.current).forEach((event) => universe.current.addEvent(event));

    // Update our active index
    index.current = events.length;

    // Save to state and trigger a re-render
    setState(universe.current.serialize());
  }, [events]);

  useEffect(() => {
    if (!snapshot || !storage.taxComplianceDeviceId) return;

    universe.current = new UniverseBuilder({
      ...snapshot,
      deviceId: storage.taxComplianceDeviceId,
    });
    setState(universe.current.serialize());
  }, [snapshot, storage.taxComplianceDeviceId]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useCashUniverse = () => {
  return useContext(Context);
};

export const useCashApi = () => {
  const dispatch = useDispatch();
  const [storage] = useStorage();
  const staffId = useSelector((state) => state.staff?.id);

  const generateEventMeta = () => ({
    createdAt: new Date().toISOString(),
    eventId: uuid(),
    sentAt: null,
  });

  const API = {
    startDay: (amount: number) => {
      dispatch(
        actions.addCashEvent({
          type: EVENT_TYPES.START_DAY,
          amount,
          deviceId: storage.taxComplianceDeviceId || '',
          ...generateEventMeta(),
        }),
      );
    },

    updateCash: (amount: number, sourceId?: string) => {
      dispatch(
        actions.addCashEvent({
          type: EVENT_TYPES.CASH_CHANGE,
          amount,
          deviceId: storage.taxComplianceDeviceId || '',
          sourceId,
          staffId,
          ...generateEventMeta(),
        }),
      );
    },

    endDay: (amount: number) => {
      dispatch(
        actions.addCashEvent({
          type: EVENT_TYPES.END_DAY,
          deviceId: storage.taxComplianceDeviceId || '',
          amount,
          ...generateEventMeta(),
        }),
      );
    },
  };

  return API;
};
